import {
  capitalize,
  cloneDeep,
  filter,
  first,
  forEach,
  get,
  includes,
  isArray,
  isEmpty,
  last,
  omit,
  replace,
  set,
  split,
} from 'es-toolkit/compat';
import { getStainOfOrchestration, ModelInference, SlideInferenceResults } from 'interfaces/calculateFeatures';
import { Model } from 'interfaces/model';
import { useState } from 'react';

export interface ModelsByStainByType {
  [stain: string]: {
    [modelType: string]: {
      [modelUrl: string]: ModelInference;
    };
  };
}

export interface ModelUserPreferences {
  rnaCountMode?: 'legacy' | 'split';
  readAs?: string;
  numBins?: number; // This is not a model preference, but used for intensity binning TODO: refactor
  mapValuesToBins?: any[];
  useClassesFromIgnore?: boolean;
  classesToIgnore?: string[];
  classesToUse?: string[];
  transferAreasToRegisteredStains?: boolean;
  segmentationCompartments?: string[];
  segmentationDilationsUm?: number[];
  countBins?: Array<{ minCount?: number; maxCount?: number }>;
  // Which positive Score row (1-indexed) defines the Instance Count Positivity threshold.
  positivityThresholdScore?: number;
}

export interface ModelPreferencesById {
  [modelId: string]: ModelUserPreferences;
}

/**
 * Hook for managing model preferences state
 */
export const useModelPreferences = () => {
  const [modelPreferencesByModelId, setModelPreferencesState] = useState<ModelPreferencesById>({});

  const setModelPreferencesByModelId = (modelId: string, userPreferences: ModelUserPreferences) => {
    if (isEmpty(userPreferences)) {
      setModelPreferencesState((prev) => {
        const newModelClassificationByModelId = cloneDeep(prev);
        delete newModelClassificationByModelId[modelId];
        return newModelClassificationByModelId;
      });
    } else {
      setModelPreferencesState((prev) => ({
        ...prev,
        [modelId]: userPreferences,
      }));
    }
  };

  const setIntensityClassificationBinning = (
    modelId: string,
    binning: { numBins?: number; mapValuesToBins?: any[] }
  ) => {
    setModelPreferencesState((prev) => ({
      ...prev,
      [modelId]: {
        ...prev[modelId],
        numBins: binning?.numBins,
        mapValuesToBins: binning?.mapValuesToBins,
      },
    }));
  };

  return {
    modelPreferencesByModelId,
    setModelPreferencesState,
    setModelPreferencesByModelId,
    setIntensityClassificationBinning,
  };
};

// example to modelUrl - "artifact://models/e2e/Seagen_B6A/Lung/PDL1/segmentation/2695841c?hash_data=1dbcc14147d685590257aa679be77345"
// example to modelId - "2695841c"
export const getModelId = (modelUrl: string) => first(split(last(split(modelUrl, '/')), '?'));

export const cellSegmentationGeometryValues = {
  nuclear: 'nuclear_geometry',
  cell: 'cell_geometry',
} as const;

export type CellSegmentationGeometryValue =
  (typeof cellSegmentationGeometryValues)[keyof typeof cellSegmentationGeometryValues];

export const getCellSegmentationGeometries = (model?: Pick<Model, 'meta'> | null) => {
  const rawGeometries =
    model?.meta?.cellSegmentationGeometries ??
    ((model?.meta as Record<string, unknown> | undefined)?.['cell_segmentation_geometries'] as unknown);

  return isArray(rawGeometries)
    ? filter(rawGeometries, (geometry): geometry is string => typeof geometry === 'string')
    : [];
};

export const hasCellSegmentationGeometry = (
  model: Pick<Model, 'meta'> | null | undefined,
  geometry: CellSegmentationGeometryValue
) => includes(getCellSegmentationGeometries(model), geometry);

export const formatCellSegmentationGeometryLabel = (geometry: string) => {
  switch (geometry) {
    case cellSegmentationGeometryValues.nuclear:
      return 'Nuclear geometry';
    case cellSegmentationGeometryValues.cell:
      return 'Cell geometry';
    default:
      return capitalize(replace(geometry, /_/g, ' '));
  }
};

// example to modelUrl - "artifact://tsm/{slide_id}.pickle?latest&meta.deps.model_id=75f8b1dc&meta.deps.override_annotations_timestamp=2023-08-27 14:48:53"
// example to override_annotations_timestamp - "2023-08-27 14:48:53"
export const getOverrideAnnotationsTimestamp = (modelUrl: string) => {
  return last(split(modelUrl, 'override_annotations_timestamp=')) ?? 'None';
};

/**
 * Helper to organize inference models by stain type and model type.
 *
 * This function transforms a flat array of inference models into a nested structure
 * that groups models by stain type, then by model type, then by model URL.
 * Each model includes its orchestrations.
 *
 * @param slidesById - Slides indexed by slideId for stain lookup
 * @param inferenceModels - Array of inference models to organize
 * @returns Organized models structure grouped by stain and type
 */
export const getModelsByStain = (
  slidesById: Record<string, SlideInferenceResults>,
  inferenceModels: ModelInference[] = []
): ModelsByStainByType => {
  const modelsByStain: ModelsByStainByType = {};

  forEach(inferenceModels, (inferenceModel) => {
    forEach(inferenceModel?.orchestrations, (orchestration) => {
      const stain = getStainOfOrchestration(slidesById, orchestration?.slideIds);
      const modelType = inferenceModel.modelType;
      const modelUrl = inferenceModel.modelUrl;

      // Initialize nested structure if not already present
      set(modelsByStain, [stain], get(modelsByStain, [stain], {}));
      set(modelsByStain, [stain, modelType], get(modelsByStain, [stain, modelType], {}));
      set(
        modelsByStain,
        [stain, modelType, modelUrl],
        get(modelsByStain, [stain, modelType, modelUrl], {
          ...omit(inferenceModel, 'orchestrations'),
          orchestrations: [],
        })
      );

      modelsByStain[stain][modelType][modelUrl].orchestrations.push(orchestration);
    });
  });

  return modelsByStain;
};
